import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import {
    buildExpensePieData,
    expenseDisplayFactor,
    type ExpenseTypeTotal,
} from '../../domain/statisticsExpenses';
import ChartPlaceholder from './ChartPlaceholder';

interface ExpensesByTypeChartProps {
    readonly data: readonly ExpenseTypeTotal[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
    readonly exchangeRate?: number;
}

function ExpensesByTypeChart({
    data,
    loading = false,
    currency = 'LEI',
    exchangeRate = 1,
}: ExpensesByTypeChartProps): JSX.Element {
    const { t } = useTranslation();

    const pieData = buildExpensePieData(data, expenseDisplayFactor(currency, exchangeRate), type =>
        t(`statistics.expenses.types.${type}`, type),
    );

    if (loading || pieData.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={pieData.length > 0} height={350} />;
    }

    const suffix =
        currency === 'EUR' ? t('statistics.currencyEur', ' EUR') : t('statistics.currencyLei', ' Lei');

    const options: Highcharts.Options = {
        chart: { type: 'pie', height: 350 },
        title: { text: undefined },
        tooltip: {
            pointFormat: `<b>{point.percentage:.1f}%</b><br/>{point.y}${suffix}`,
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                },
                showInLegend: true,
            },
        },
        series: [
            {
                type: 'pie',
                name: t('statistics.expenses.total', 'Expenses'),
                data: pieData,
            },
        ],
        credits: { enabled: false },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default ExpensesByTypeChart;
